import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { ReservationDetailView } from "@/components/shop/ReservationDetailView";
import {
  fetchReservation,
  type ReservationStatus,
} from "@/features/storefront";

export const Route = createFileRoute("/shop/r/$token")({
  component: ShopReservationStatus,
});

type LoadState =
  | { phase: "loading" }
  | { phase: "ready"; reservation: ReservationStatus }
  | { phase: "notfound" }
  | { phase: "error" };

function ShopReservationStatus() {
  const { token } = Route.useParams();
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const reservation = await fetchReservation(token);
        if (cancelled) return;
        setState(
          reservation ? { phase: "ready", reservation } : { phase: "notfound" },
        );
      } catch {
        if (!cancelled) setState({ phase: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.phase === "loading") {
    return <p className="py-12 text-center text-muted-foreground">Loading…</p>;
  }
  if (state.phase === "notfound") {
    return (
      <p className="py-12 text-center text-muted-foreground">
        Reservation not found. Double-check your link.
      </p>
    );
  }
  if (state.phase === "error") {
    return (
      <p className="py-12 text-center text-destructive">
        Couldn’t load this reservation.
      </p>
    );
  }

  const r = state.reservation;
  return (
    <ReservationDetailView
      reservation={r}
      token={token}
      onReservationUpdate={(next) =>
        setState({ phase: "ready", reservation: next })
      }
      subtitle={
        <>
          Bookmark this page to check the status. The seller will follow up by
          email{r.buyerName ? `, ${r.buyerName}` : ""}.
        </>
      }
      footer={
        <Link
          to="/shop/inventory"
          className="inline-block text-sm font-medium text-primary hover:underline"
        >
          Keep browsing
        </Link>
      }
    />
  );
}
